import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CreditCard, AlertCircle } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const AddPaymentMethodPage: React.FC = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    accountName: ''
  });

  const paymentMethodInfo = {
    googlepay: {
      name: 'Google Pay',
      icon: '🟢',
      description: 'Connect your Google Pay account',
      fields: ['email']
    },
    applepay: {
      name: 'Apple Pay',
      icon: '🍎',
      description: 'Connect your Apple Pay account',
      fields: ['email']
    },
    samsungpay: {
      name: 'Samsung Pay',
      icon: '📱',
      description: 'Connect your Samsung Pay account',
      fields: ['phone']
    },
    paypal: {
      name: 'PayPal',
      icon: '💙',
      description: 'Connect your PayPal account',
      fields: ['email']
    }
  };

  const currentMethod = paymentMethodInfo[type as keyof typeof paymentMethodInfo];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulate API call to add payment method
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert(`${currentMethod?.name} account connected successfully!`);
      navigate('/payment-methods');
    } catch (error) {
      alert('Failed to connect payment method. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!currentMethod) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-6 text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Invalid Payment Method</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">The requested payment method is not supported.</p>
          <Button onClick={() => navigate('/payment-methods')} variant="primary">
            Back to Payment Methods
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/payment-methods')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Add {currentMethod.name}</h1>
      </div>

      <div className="px-4 pb-8">
        <Card className="p-6">
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">{currentMethod.icon}</div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">{currentMethod.name}</h2>
            <p className="text-gray-600 dark:text-gray-300">{currentMethod.description}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {currentMethod.fields.includes('email') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter your email address"
                  required
                />
              </div>
            )}

            {currentMethod.fields.includes('phone') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter your phone number"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Account Name (Optional)
              </label>
              <input
                type="text"
                value={formData.accountName}
                onChange={(e) => setFormData(prev => ({ ...prev, accountName: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter a name for this payment method"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              variant="gradient"
              size="lg"
              className="w-full mt-6"
            >
              {loading ? (
                'Connecting...'
              ) : (
                <>
                  <CreditCard size={20} className="mr-2" />
                  Connect {currentMethod.name}
                </>
              )}
            </Button>
          </form>
        </Card>

        {/* Security Notice */}
        <Card className="p-4 mt-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium">Secure Connection</span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Your payment information is encrypted and secure. We never store your payment credentials on our servers.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default AddPaymentMethodPage;